/**
 * Stage 02 — Molecules section for the Atomic Design showcase.
 * SearchField, FormField, IlmuChip, UserChip, NavItem, TabBar, Stat,
 * CapacityBar, Toast, Notification, CalendarCell, Breadcrumb, ToolCall.
 */

import { Avatar } from '../../components/primitives/Avatar'
import { Icon } from '../../components/primitives/Icon'
import { IlmuMark } from '../../components/primitives/IlmuMark'
import { Kbd } from '../../components/primitives/Kbd'
import { CalCell, Grid3, PrimitiveCard, SectionHeader } from './shared'

export function MoleculesSection() {
  return (
    <section id="molecules" style={{ marginBottom: 80, scrollMarginTop: 24 }}>
      <SectionHeader
        num="02"
        name="Molecules"
        tag="Atoms in concert"
        stage="molecules"
        desc="Two or three atoms wired together to do one specific job — like a search field (icon + input + kbd) or a form field (label + input + help text)."
      />

      <Grid3>
        {/* Search field */}
        <PrimitiveCard
          name="Search field"
          tag="Molecule"
          desc="Icon + input + ⌘K hint, fills the topbar."
          uses={[
            { stage: 'atom', label: 'Icon' },
            { stage: 'atom', label: 'Input' },
            { stage: 'atom', label: 'Kbd' },
          ]}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'var(--surface-2)',
              borderRadius: 9,
              padding: '7px 12px',
              width: '100%',
              maxWidth: 340,
              color: 'var(--text-3)',
              fontSize: 13,
            }}
          >
            <Icon name="search" size={14} />
            <input
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: 13,
                color: 'var(--text)',
              }}
              placeholder="Search or ask ILMU…"
            />
            <Kbd>⌘K</Kbd>
          </div>
        </PrimitiveCard>

        {/* Form field */}
        <PrimitiveCard
          name="Form field"
          tag="Molecule"
          desc="Label + control + helper text. The repeating unit of every form."
          uses={[
            { stage: 'atom', label: 'Label' },
            { stage: 'atom', label: 'Input' },
          ]}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>
              Start date
            </span>
            <input
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border-strong)',
                borderRadius: 9,
                padding: '9px 12px',
                fontSize: 13.5,
                width: '100%',
                outline: 'none',
              }}
              defaultValue="Mon 22 Jun 2026"
            />
            <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              First working day of your leave.
            </span>
          </div>
        </PrimitiveCard>

        {/* ILMU chip */}
        <PrimitiveCard
          name="ILMU chip"
          tag="Molecule"
          desc="Mark + label. Used inline anywhere ILMU has acted or has an opinion."
          uses={[
            { stage: 'atom', label: 'ILMU Mark' },
            { stage: 'atom', label: 'Badge' },
          ]}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 9px 3px 5px',
              borderRadius: 999,
              background: '#ECEDF6',
              color: '#00205b',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              border: '1px solid rgba(0,32,91,.08)',
            }}
          >
            <IlmuMark size={13} aria-hidden />
            ILMU
          </span>
        </PrimitiveCard>

        {/* User chip */}
        <PrimitiveCard
          name="User chip"
          tag="Molecule"
          desc="Avatar + name + role. Read-only people reference."
          uses={[{ stage: 'atom', label: 'Avatar' }]}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 10px',
              borderRadius: 9,
              background: 'var(--surface-2)',
            }}
          >
            <Avatar name="Aisyah Rahman" size={24} />
            <span>
              <span style={{ fontSize: 12.5, fontWeight: 500, display: 'block' }}>
                Aisyah Rahman
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-2)' }}>SWE · Platform</span>
            </span>
          </span>
        </PrimitiveCard>

        {/* Nav item */}
        <PrimitiveCard
          name="Nav item"
          tag="Molecule"
          desc="Icon + label, optional count badge, active state on Baby Blue Tint."
          col
          uses={[
            { stage: 'atom', label: 'Icon' },
            { stage: 'atom', label: 'Badge' },
          ]}
        >
          <div style={{ maxWidth: 220, margin: '0 auto', width: '100%' }}>
            {[
              { label: 'Home', icon: 'home', active: true },
              { label: 'Approvals', icon: 'checklist', badge: '5' },
              { label: 'Team calendar', icon: 'calendar_today' },
            ].map(({ label, icon, active, badge }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  color: active ? 'var(--steel-blue-3)' : 'var(--text-2)',
                  background: active ? 'var(--baby-blue-tint)' : 'transparent',
                  fontSize: 13.5,
                  fontWeight: 500,
                }}
              >
                <span
                  style={{
                    width: 18,
                    height: 18,
                    color: active ? 'var(--steel-blue)' : 'var(--text-3)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <Icon name={icon as Parameters<typeof Icon>[0]['name']} size={16} />
                </span>
                {label}
                {badge ? (
                  <span
                    style={{
                      marginLeft: 'auto',
                      background: 'var(--space-cadet)',
                      color: '#fff',
                      fontSize: 10.5,
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: 999,
                    }}
                  >
                    {badge}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </PrimitiveCard>

        {/* Tab bar */}
        <PrimitiveCard name="Tab bar" tag="Molecule" desc="Underline-style switcher between sibling views.">
          <div
            style={{
              display: 'flex',
              gap: 2,
              borderBottom: '1px solid var(--border)',
              width: '100%',
            }}
          >
            {['Pending', 'Approved', 'Escalated'].map((tab, i) => (
              <span
                key={tab}
                style={{
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: i === 0 ? 'var(--text)' : 'var(--text-2)',
                  borderBottom: `2px solid ${i === 0 ? 'var(--space-cadet)' : 'transparent'}`,
                  marginBottom: -1,
                  cursor: 'pointer',
                }}
              >
                {tab}
              </span>
            ))}
          </div>
        </PrimitiveCard>

        {/* Stat */}
        <PrimitiveCard name="Stat" tag="Molecule" desc="A single tabular number with a mono caption.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div
              style={{
                fontSize: 28,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              12.5
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--text-2)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
              }}
            >
              Days remaining
            </div>
          </div>
        </PrimitiveCard>

        {/* Capacity bar */}
        <PrimitiveCard
          name="Capacity bar"
          tag="Molecule"
          desc="Name + bar + % — the staple of team-coverage views."
          col
          uses={[{ stage: 'atom', label: 'Label' }]}
        >
          {[
            { team: 'Platform', pct: 18, variant: 'ok' },
            { team: 'Operations', pct: 32, variant: 'warn' },
            { team: 'People', pct: 50, variant: 'danger' },
          ].map(({ team, pct, variant }) => (
            <div
              key={team}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', width: '100%' }}
            >
              <span style={{ width: 80, fontSize: 12, color: 'var(--text-2)', flexShrink: 0 }}>
                {team}
              </span>
              <div
                style={{
                  flex: 1,
                  height: 8,
                  background: 'var(--surface-2)',
                  borderRadius: 4,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    background:
                      variant === 'warn'
                        ? 'var(--warning)'
                        : variant === 'danger'
                          ? 'var(--danger)'
                          : 'var(--steel-blue)',
                    borderRadius: 4,
                  }}
                />
              </div>
              <span
                style={{
                  width: 46,
                  textAlign: 'right',
                  fontSize: 12,
                  color: 'var(--text-2)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {pct}%
              </span>
            </div>
          ))}
        </PrimitiveCard>

        {/* Toast */}
        <PrimitiveCard
          name="Toast"
          tag="Molecule"
          desc="Transient confirmation. Bottom-right, 4 second lifespan."
          uses={[{ stage: 'atom', label: 'Icon' }]}
        >
          <div
            style={{
              background: 'var(--space-cadet)',
              color: '#fff',
              padding: '11px 14px',
              borderRadius: 11,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              maxWidth: 320,
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--pear)',
                boxShadow: '0 0 8px var(--pear)',
                flexShrink: 0,
              }}
            />
            Request submitted. ILMU notified Hafiz.
          </div>
        </PrimitiveCard>

        {/* Notification */}
        <PrimitiveCard name="Notification" tag="Molecule" desc="Dot + title + subtitle + time.">
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px 12px',
              alignItems: 'flex-start',
              border: '1px solid var(--border)',
              borderRadius: 10,
              background: 'var(--surface)',
              width: '100%',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--steel-blue)',
                marginTop: 6,
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>Hafiz approved your leave</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
                Jun 22–26 · ILMU resolved the conflict with Daniel.
              </div>
            </div>
            <span
              style={{
                fontSize: 10.5,
                color: 'var(--text-3)',
                fontFamily: 'var(--font-mono)',
                flexShrink: 0,
              }}
            >
              2m
            </span>
          </div>
        </PrimitiveCard>

        {/* Calendar cell */}
        <PrimitiveCard
          name="Calendar cell"
          tag="Molecule"
          desc="Day number + 0-2 chips. Variants: weekend, holiday, blackout, today."
          uses={[{ stage: 'atom', label: 'Badge' }]}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            <CalCell day={9} />
            <CalCell day={18} variant="today" evt="You · AL" />
            <CalCell day={31} variant="holiday" evt="Wesak" />
            <CalCell day={2} variant="blackout" evt="Launch" />
          </div>
        </PrimitiveCard>

        {/* Breadcrumb */}
        <PrimitiveCard
          name="Breadcrumb"
          tag="Molecule"
          desc="Trail of links, current page in --text."
          uses={[{ stage: 'atom', label: 'Icon' }]}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 8,
              fontSize: 13,
              color: 'var(--text-2)',
              alignItems: 'center',
            }}
          >
            <span>Home</span>
            <Icon name="chevron_right" size={12} />
            <span>Approvals</span>
            <Icon name="chevron_right" size={12} />
            <span style={{ color: 'var(--text)', fontWeight: 500 }}>Hari Raya conflict</span>
          </div>
        </PrimitiveCard>

        {/* Tool call */}
        <PrimitiveCard
          name="Tool call"
          tag="Molecule · AI"
          desc="Shows ILMU's reasoning — the tool, its args, and the result."
          dark
          uses={[
            { stage: 'atom', label: 'Badge' },
            { stage: 'atom', label: 'ILMU Mark' },
          ]}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: '#fff',
              lineHeight: 1.6,
              width: '100%',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: 'var(--pear)',
                  boxShadow: '0 0 8px var(--pear)',
                }}
              />
              <span style={{ color: 'var(--pear)', fontWeight: 600 }}>get_balance</span>
              <span style={{ color: 'rgba(255,255,255,.55)' }}>
                (employee_id: "aisyah", leave_type: "annual")
              </span>
            </div>
            <div
              style={{
                marginTop: 6,
                paddingLeft: 12,
                color: 'rgba(255,255,255,.75)',
              }}
            >
              <span style={{ color: 'rgba(255,255,255,.5)' }}>remaining:</span> 12.5{'   '}
              <span style={{ color: 'rgba(255,255,255,.5)' }}>used:</span> 5.5{'   '}
              <span style={{ color: 'rgba(255,255,255,.5)' }}>pending:</span> 2
            </div>
          </div>
        </PrimitiveCard>
      </Grid3>
    </section>
  )
}
